// Mirrors PaymentPlan.toJSON() in 51trips-colab-api: a pack sold through Stripe
// in one or more negotiated installments. The pack itself only shows up in
// /packages once the first installment is paid (it's activated whole then).

export type PaymentInstallmentStatus = 'PENDING' | 'PAID' | 'CANCELED'

export type PaymentPlanStatus = 'PENDING' | 'PARTIAL' | 'PAID'

export type PaymentInstallment = {
    id: string
    amount: number  // VAT included
    dueDate: number  // timestamp ms
    status: PaymentInstallmentStatus
    paymentUrl?: string
    paidAt?: number
}

export type PaymentPlan = {
    id: string
    agencyId: string
    unitPrice: number  // VAT excluded, like TripbookPackage.unitPrice
    quantity: number
    date: number  // timestamp ms
    numberOfPagesIncluded?: number
    vatRate: number  // e.g. 0.21
    currency: string  // ISO 4217, lowercase
    netAmount: number
    grossAmount: number
    status: PaymentPlanStatus
    installments: PaymentInstallment[]
    packageId?: string
    createdAt: number
    createdBy?: string
}

export type InstallmentDraft = {
    amount: number
    dueDate: number
}

export type CreatePaymentPlanPayload = {
    unitPrice: number
    quantity: number
    date: number
    numberOfPagesIncluded?: number
    vatRate: number
    currency: string
    installments: InstallmentDraft[]
}

// Gross total in cents, rounded the same way as PaymentPlan.grossAmountCents in
// the backend: the installments must add up to exactly this.
export function grossAmountCents(unitPrice: number, quantity: number, vatRate: number): number {
    return Math.round(unitPrice * quantity * (1 + vatRate) * 100)
}

export function toCents(amount: number): number {
    return Math.round(amount * 100)
}

// Splits `totalCents` into `count` equal parts; the leftover cents go to the last one.
export function splitEvenly(totalCents: number, count: number): number[] {
    if (count <= 0) return []
    const base = Math.floor(totalCents / count)
    return Array.from({length: count}, (_, i) => i === count - 1 ? totalCents - base * (count - 1) : base)
}

export function isOverdue(installment: PaymentInstallment, now: number = Date.now()): boolean {
    return installment.status === 'PENDING' && installment.dueDate < now
}
